import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Wand2 } from 'lucide-react';
import { getCapabilities, type CapabilityGroup } from '@/lib/assistant';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const capabilityKeys = {
  all: ['assistant', 'capabilities'] as const,
};

function matches(query: string, group: CapabilityGroup, item: CapabilityGroup['items'][number]) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.phrase.toLowerCase().includes(q) ||
    item.example.toLowerCase().includes(q) ||
    group.label.toLowerCase().includes(q)
  );
}

/**
 * Everything the assistant can do for you, as buttons.
 *
 * A list you have to read and then retype is a worse version of the same
 * information: clicking runs the example. The contents come from the server's
 * permission filter, the same one that builds the model's tool list, so the
 * palette can never offer a command that would be refused when pressed.
 */
export function CommandPalette({ onRun, busy }: { onRun: (prompt: string) => void; busy?: boolean }) {
  const [query, setQuery] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: capabilityKeys.all,
    queryFn: getCapabilities,
    staleTime: 5 * 60_000,
  });

  const groups = useMemo(
    () =>
      (data?.groups ?? [])
        .map((g) => ({ ...g, items: g.items.filter((i) => matches(query, g, i)) }))
        .filter((g) => g.items.length > 0),
    [data, query],
  );

  const total = useMemo(
    () => (data?.groups ?? []).reduce((n, g) => n + g.items.length, 0),
    [data],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${total} commands…`}
          aria-label="Search commands"
          className="h-8 pl-8 text-xs"
        />
      </div>

      {groups.length === 0 ? (
        <p className="px-1 py-6 text-center text-xs text-muted-foreground">
          Nothing matches “{query}”.
        </p>
      ) : (
        groups.map((group) => (
          <div key={group.domain}>
            <p className="px-1 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            <div className="space-y-1">
              {group.items.map((item) => (
                <button
                  key={item.phrase}
                  type="button"
                  disabled={busy}
                  onClick={() => onRun(item.example)}
                  className={cn(
                    'group flex w-full cursor-pointer items-start gap-2 rounded-md border border-border px-2.5 py-2 text-left',
                    'hover:border-hygge/40 hover:bg-hygge/5 disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-xs font-medium text-foreground first-letter:uppercase">
                        {item.phrase}
                      </span>
                      {/* Marked, not hidden: knowing which commands end in a
                          confirmation is the point of the confirmation. */}
                      {item.writes && (
                        <span className="shrink-0 rounded bg-muted px-1 py-px text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                          asks first
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {item.example}
                    </span>
                  </span>
                  <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-hygge" />
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
